import { signal, effect } from '@preact/signals';
import type { Signal } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

export type PlaylistItem = {
  ref: string;
  book: string;
  chapter: number;
  surface: string;
  why: string;
};

export type Playlist = {
  id: string;
  name: string;
  prompt: string;
  items: PlaylistItem[];
  createdAt: number;
};

const STORAGE_KEY = '_pl1';
const MAX_SAVED = 20;

function readStored(): Playlist[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPlaylist);
  } catch {
    return [];
  }
}

function isPlaylistItem(v: unknown): v is PlaylistItem {
  if (!v || typeof v !== 'object') return false;
  const i = v as Record<string, unknown>;
  return (
    typeof i.ref === 'string' &&
    typeof i.book === 'string' &&
    typeof i.chapter === 'number' &&
    typeof i.surface === 'string' &&
    typeof i.why === 'string'
  );
}

function isPlaylist(v: unknown): v is Playlist {
  if (!v || typeof v !== 'object') return false;
  const p = v as Record<string, unknown>;
  return (
    typeof p.id === 'string' &&
    typeof p.name === 'string' &&
    typeof p.prompt === 'string' &&
    typeof p.createdAt === 'number' &&
    Array.isArray(p.items) &&
    (p.items as unknown[]).every(isPlaylistItem)
  );
}

export const savedPlaylists: Signal<Playlist[]> = signal<Playlist[]>(readStored());
export const currentPlaylist: Signal<{ playlist: Playlist; index: number } | null> = signal<{ playlist: Playlist; index: number } | null>(null);
export const playlistBuilding: Signal<boolean> = signal<boolean>(false);
export const playlistError: Signal<string | null> = signal<string | null>(null);

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(savedPlaylists.value));
  });
}

type ApiPlaylistItem = {
  ref: string;
  book?: string;
  chapter?: number;
  surface: string;
  why: string;
};

type ApiResponse = {
  name: string;
  items: ApiPlaylistItem[];
};

function normalizeItem(item: ApiPlaylistItem): PlaylistItem {
  const parts = item.ref.split('.');
  const book = item.book ?? (parts[0] ?? '');
  const chapter = item.chapter ?? (parts[1] ? parseInt(parts[1], 10) : 0);
  return {
    ref: item.ref,
    book,
    chapter,
    surface: item.surface,
    why: item.why,
  };
}

export async function buildPlaylist(prompt: string): Promise<Playlist | null> {
  playlistBuilding.value = true;
  playlistError.value = null;

  try {
    const res = await fetch('/api/playlist/build', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt }),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
      throw new Error(errBody.error ?? `HTTP ${res.status}`);
    }

    const data = await res.json() as ApiResponse;

    const playlist: Playlist = {
      id: crypto.randomUUID(),
      name: data.name,
      prompt,
      items: data.items.map(normalizeItem),
      createdAt: Date.now(),
    };

    savePlaylist(playlist);
    currentPlaylist.value = { playlist, index: 0 };

    return playlist;
  } catch (err) {
    playlistError.value = err instanceof Error ? err.message : 'Failed to build playlist';
    return null;
  } finally {
    playlistBuilding.value = false;
  }
}

export function setPlaylistIndex(i: number): void {
  const cur = currentPlaylist.peek();
  if (!cur) return;
  const clamped = Math.max(0, Math.min(i, cur.playlist.items.length - 1));
  currentPlaylist.value = { ...cur, index: clamped };
}

export function advancePlaylist(): boolean {
  const cur = currentPlaylist.peek();
  if (!cur) return false;
  const next = cur.index + 1;
  if (next >= cur.playlist.items.length) return false;
  currentPlaylist.value = { ...cur, index: next };
  return true;
}

export function savePlaylist(p: Playlist): void {
  const existing = savedPlaylists.peek().filter((s) => s.id !== p.id);
  const next = [p, ...existing];
  savedPlaylists.value = next.slice(0, MAX_SAVED);
}

export function deletePlaylist(id: string): void {
  savedPlaylists.value = savedPlaylists.peek().filter((p) => p.id !== id);
  const cur = currentPlaylist.peek();
  if (cur?.playlist.id === id) {
    currentPlaylist.value = null;
  }
}
